/* eslint-disable react/display-name */

import { memo, useContext, useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { changeResponseTagState } from '@aytm/app/stats/page/comments/actions'

import { OEContext } from '@aytm/app/stats/page/comments'

import Popup from '@aytm/lib/rfd-ui/components/basic/popup'
import ClickOutside from '@aytm/lib/rfd-ui/components/basic/click-outside'
import SentimentIcon from '@aytm/app/stats/page/comments/comments/components/OEQuestionResponse/SentimentIcon'

import style from '@aytm/app/stats/page/comments/comments/styles/Sentiments.module.scss'

const sentimentValues = [
  {
    sentiment: 'None',
    style: {
      left: '-20px',
      top: '-60px'
    },
    value: 0
  },
  {
    sentiment: 'Positive',
    style: {
      left: '-30px',
      top: '-38px'
    },
    value: 3
  },
  {
    sentiment: 'Mixed',
    style: {
      left: '-35px',
      top: '-15px'
    },
    value: 4
  },
  {
    sentiment: 'Negative',
    style: {
      left: '-30px',
      top: '8px'
    },
    value: 1
  },
  {
    sentiment: 'Neutral',
    style: {
      left: '-20px',
      top: '30px'
    },
    value: 2
  }
]

const Sentiment = memo(({ className, sentiment = 'None', keyBase, path, responseId, onUpdateSchema }) => {
  const { searchState, commentStatusesState, dispatch, dispatchCommentStatuses, onFetchApiKey } = useContext(OEContext)

  const { request } = searchState

  const sentimentStatus = commentStatusesState.sentiment[responseId] || {}

  const [isActive, setIsActive] = useState(false)
  const [inProgress, setInProgress] = useState(false)

  const [activeLabel, setActiveLabel] = useState(null)

  const sentimentPopupRef = useRef(null)
  const intersectionObserverRef = useRef(null)

  const onChangeSentiment = async value => {
    if (null === value) return

    setInProgress(true)
    await changeResponseTagState({
      tag: {
        tag: value.sentiment,
        type: 'sentiment',
        value: value.value,
        sentiment: value.sentiment.toUpperCase(),
        currentValue: sentiment
      },
      type: 'SENTIMENT_CHANGED',
      path,
      request,
      responseId,
      action: 'create',
      onFetchApiKey,
      onUpdateSchema,
      dispatch,
      dispatchCommentStatuses
    })
    setInProgress(false)
    setIsActive(false)
  }

  const onClickOutside = () => {
    setIsActive(false)
  }

  useEffect(() => {
    if (isActive) {
      intersectionObserverRef.current ||= new window.IntersectionObserver(
        entries =>
          entries.forEach(entry => {
            if (entry.target != sentimentPopupRef.current) return
            !entry.isIntersecting && setIsActive(false)
          }),
        {
          rootMargin: '-200px 0px 0px 0px'
        }
      )
      intersectionObserverRef.current.observe(sentimentPopupRef.current)
    } else if (null !== intersectionObserverRef.current) intersectionObserverRef.current.disconnect()

    return () => {
      if (null !== intersectionObserverRef.current) intersectionObserverRef.current.disconnect()
    }
  }, [isActive])

  return (
    <ClickOutside onClickOutside={() => onClickOutside()}>
      <div
        className={classNames(className, style[`sentiment-container`], {
          [style['is-active']]: isActive
        })}
        onClick={() => setIsActive(true)}
      >
        {sentimentStatus[path] ? (
          <Popup
            width="215px"
            direction="topleft"
            popupOffset={12}
            portal
            show={'error' == sentimentStatus[path].type}
            hideOtherPopupsOnShow
            content={() => (
              <div className={style['response-header-notification-text']}>{sentimentStatus[path].message}</div>
            )}
          />
        ) : null}

        {isActive && !inProgress ? (
          <div className={style['sentiment-container-label']}>{activeLabel || sentiment}</div>
        ) : null}

        {inProgress ? (
          <i
            className={classNames('icon-aytmfd-refresh-v2 aytmfd-spin', {
              [style['sentiment-container-item-loading']]: isActive
            })}
          />
        ) : (
          <SentimentIcon
            className={style[`sentiment-container-item`]}
            iconClassName={style[`sentiment-container-item-icon`]}
            sentiment={sentiment}
          />
        )}

        <div ref={sentimentPopupRef} className={style['sentiment-select']}>
          {sentimentValues.map(sentimentValue => (
            <SentimentIcon
              key={`${keyBase}-${sentimentValue.sentiment}`}
              className={classNames(style[`sentiment-select-item`], {
                [style['is-active']]: isActive
              })}
              iconClassName={style[`sentiment-select-item-icon`]}
              sentiment={sentimentValue.sentiment}
              style={isActive ? sentimentValue.style : {}}
              onMouseOver={() => setActiveLabel(sentimentValue.sentiment)}
              onMouseOut={() => {
                setActiveLabel(null)
              }}
              onClick={isActive ? () => onChangeSentiment(sentimentValue) : undefined}
            />
          ))}
        </div>
      </div>
    </ClickOutside>
  )
})

Sentiment.propTypes = {
  className: PropTypes.string,
  sentiment: PropTypes.string,
  keyBase: PropTypes.string,
  path: PropTypes.string,
  responseId: PropTypes.number,
  onUpdateSchema: PropTypes.func
}

export default Sentiment
